import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertCircle,
  Check,
  Heart,
  Loader2,
  Palette,
  PenLine,
  Search,
  Shield,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

// ── Server build-progress contract ──────────────────────────────────────────
// Mirrors PersonaBuildProgress from server/services/personaBuild.ts, attached
// to the personas list response only while buildStatus === "building".
export type BuildStepState = "waiting" | "working" | "complete" | "failed" | "none";
export type BuildProgress = {
  allTerminal: boolean;
  profile: BuildStepState;
  image?: BuildStepState;
  enrichment: BuildStepState;
  characters: {
    state: BuildStepState;
    created: number;
    target: number;
    settled: number;
    /** Usable saved avatar images. Missing counts are treated as zero. */
    ready?: number;
    failed?: number;
  };
  research: { state: BuildStepState; done: number; failed: number; total: number };
  blockedStage?: "profile" | "sketch" | "traits" | "fingerprint" | "image" | "research" | "characters";
};

/**
 * Synthesized progress for the rare row that is generating but has no
 * server-attached progress yet (progress fetch failed, or a legacy
 * status-only "generating" persona): profile + portrait run first.
 */
export function fallbackBuildProgress(): BuildProgress {
  return {
    allTerminal: false,
    profile: "working",
    image: "working",
    enrichment: "waiting",
    characters: { state: "waiting", created: 0, target: 5, settled: 0, ready: 0 },
    research: { state: "waiting", done: 0, failed: 0, total: 0 },
  };
}

// Same design constants as the finished persona card (personas.tsx) so the
// build card sits in the grid with an identical footprint and palette.
const SERIF = { fontFamily: `'Iowan Old Style', 'Palatino Linotype', Georgia, 'Times New Roman', serif` };
const BAND = "#231c16";
const CARD_MIN_H = "min-h-[480px]";

/**
 * Persona names are long descriptive phrases, so cards never truncate them.
 * Instead the type steps down by name length so even very long names fit
 * fully inside the fixed-height hero band. Shared by the finished, failed,
 * and building card variants so all three stay visually consistent.
 */
export type PersonaNameTier = "base" | "medium" | "small" | "xs";
export function personaNameTier(name: string | null | undefined): PersonaNameTier {
  const len = (name ?? "").trim().length;
  if (len <= 30) return "base";
  if (len <= 55) return "medium";
  if (len <= 90) return "small";
  return "xs";
}

/** Name type classes for the compact band variants (failed + building cards). */
export const PERSONA_NAME_TIER_COMPACT: Record<PersonaNameTier, string> = {
  base: "text-[20px] leading-[24px]",
  medium: "text-[15px] leading-[19px]",
  small: "text-[13px] leading-[17px]",
  xs: "text-[12px] leading-[15px]",
};

type StepKey = "profile" | "image" | "enrichment" | "characters" | "research";

const STEPS: { key: StepKey; label: string; icon: typeof PenLine }[] = [
  { key: "profile", label: "Profile", icon: PenLine },
  { key: "image", label: "Portrait", icon: Palette },
  { key: "enrichment", label: "Traits", icon: Sparkles },
  { key: "characters", label: "Characters", icon: Users },
  { key: "research", label: "Research", icon: Search },
];

function rawState(key: StepKey, bp: BuildProgress): BuildStepState {
  switch (key) {
    case "profile": return bp.profile;
    case "image": return bp.image ?? "none";
    case "enrichment": return bp.enrichment;
    case "characters": return bp.characters.state;
    case "research": return bp.research.state;
  }
}

/**
 * What the timeline shows for a step. "none" means the server hasn't started
 * (enrichment/research kick off after the profile lands) — while the build is
 * still open those read as upcoming, not invisible. A "none" portrait step
 * (legacy rows) genuinely has no work to show and stays hidden.
 */
function displayState(key: StepKey, bp: BuildProgress): BuildStepState {
  const s = rawState(key, bp);
  if (s === "none" && key !== "image") return "waiting";
  return s;
}

/** Exactly one step narrates at a time: the first one still working. */
function activeStep(bp: BuildProgress): StepKey | null {
  for (const s of STEPS) {
    if (displayState(s.key, bp) === "working") return s.key;
  }
  return null;
}

function narrationFor(active: StepKey | null, bp: BuildProgress): string {
  switch (active) {
    case "profile": return "Writing the psychological profile…";
    case "image": return "Painting the portrait…";
    case "enrichment": return "Distilling fears, desires & triggers…";
    case "characters":
      return `Casting characters — ${readyAvatarCount(bp)} of ${bp.characters.target} avatars rendered…`;
    case "research":
      return bp.research.total > 0
        ? `Researching audience — topic ${Math.min(bp.research.done + 1, bp.research.total)} of ${bp.research.total}…`
        : "Mapping research topics…";
    default:
      return "Finishing up…";
  }
}

/**
 * Overall successful completion across the visible steps. Failed work is
 * terminal for the server lifecycle but does not masquerade as completed in
 * the percentage. Counter-steps use saved avatar images and finished research
 * topics for exact partial credit. Only ever derived from server progress —
 * never a timer.
 */
export function readyAvatarCount(bp: BuildProgress): number {
  return Math.max(0, Math.min(bp.characters.target, bp.characters.ready ?? 0));
}

export function progressPercent(bp: BuildProgress): number {
  const visible = STEPS.filter((s) => displayState(s.key, bp) !== "none");
  let sum = 0;
  for (const s of visible) {
    const st = displayState(s.key, bp);
    if (s.key === "characters") {
      sum += bp.characters.target > 0 ? readyAvatarCount(bp) / bp.characters.target : 0;
    } else if (s.key === "research") {
      if (bp.research.total > 0) {
        sum += Math.min(1, bp.research.done / bp.research.total);
      } else if (st === "complete") {
        sum += 1;
      }
    } else if (st === "complete") {
      sum += 1;
    } else if (st === "working") {
      sum += 0.35;
    }
  }
  return Math.min(100, Math.round((sum / Math.max(1, visible.length)) * 100));
}

export function interruptedNarration(bp: BuildProgress, error?: string | null): string {
  const inferred = bp.blockedStage
    ?? (STEPS.find((step) => rawState(step.key, bp) === "failed")?.key)
    ?? (STEPS.find((step) => rawState(step.key, bp) === "working")?.key);
  const saved = "Saved completed work will be kept when you retry.";
  const reason = error?.trim() ? ` Last recorded reason: ${error.trim()}` : "";
  switch (inferred) {
    case "characters":
      return `Character avatar rendering stopped with ${readyAvatarCount(bp)} of ${bp.characters.target} images saved.${reason} ${saved}`;
    case "research":
      return `Audience research stopped after ${bp.research.done} of ${bp.research.total} topics were saved.${reason} ${saved}`;
    case "traits":
    case "enrichment":
      return `Trait enrichment was interrupted.${reason} ${saved}`;
    case "image":
      return `Portrait generation was interrupted.${reason} ${saved}`;
    case "sketch":
      return `Persona sketch generation was interrupted.${reason} ${saved}`;
    case "fingerprint":
      return `Audience fingerprinting was interrupted.${reason} ${saved}`;
    case "profile":
      return `Profile generation was interrupted.${reason} ${saved}`;
    default:
      return error
        ? `Generation stopped: ${error}. ${saved}`
        : `Generation was interrupted. ${saved}`;
  }
}

// ── Micro-animations ─────────────────────────────────────────────────────────

/** Types the narration once per step; the caret keeps blinking after. */
function Typewriter({ text }: { text: string }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    const id = window.setInterval(() => {
      setN((v) => (v >= text.length ? v : v + 1));
    }, 42);
    return () => window.clearInterval(id);
  }, [text]);
  return (
    <span>
      {text.slice(0, n)}
      <span className="pb-caret inline-block w-[6px] h-[12px] translate-y-[1.5px] bg-white/60" />
    </span>
  );
}

/** Handwriting motif: pen + manuscript lines that write themselves in a loop. */
function ProfileStage() {
  return (
    <div className="flex flex-col items-start gap-2.5 w-[110px]">
      <PenLine className="h-5 w-5 text-white/35 mb-0.5" />
      {[100, 82, 92, 64].map((w, i) => (
        <span
          key={i}
          className="pb-write-line h-1.5 rounded-full bg-white/20 origin-left"
          style={{ width: `${w}%`, animationDelay: `${i * 0.55}s` }}
        />
      ))}
    </div>
  );
}

/** Portrait silhouette that progressively "develops" bottom-to-top. */
function PortraitStage() {
  return (
    <div className="relative h-[116px] w-[116px] overflow-hidden rounded-xl bg-white/[0.04] border border-white/10">
      <svg viewBox="0 0 96 96" className="pb-develop absolute inset-0 h-full w-full" aria-hidden="true">
        <circle cx="48" cy="33" r="17" fill="rgba(255,255,255,0.22)" />
        <path d="M14 96 C14 68 30 57 48 57 C66 57 82 68 82 96 Z" fill="rgba(255,255,255,0.22)" />
      </svg>
      <span className="pb-shimmer absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-transparent via-white/15 to-transparent" />
    </div>
  );
}

/** Fear / desire / trigger icons breathing while traits are distilled. */
function TraitsStage() {
  const items = [
    { icon: Shield, tint: "text-rose-300/80" },
    { icon: Heart, tint: "text-orange-300/80" },
    { icon: Zap, tint: "text-emerald-300/80" },
  ];
  return (
    <div className="flex items-center gap-3">
      {items.map(({ icon: Icon, tint }, i) => (
        <span
          key={i}
          className="pb-breathe h-10 w-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center"
          style={{ animationDelay: `${i * 0.4}s` }}
        >
          <Icon className={`h-[18px] w-[18px] ${tint}`} />
        </span>
      ))}
    </div>
  );
}

/** Five avatar slots; each pops in only when a usable avatar image is saved. */
function CharactersStage({ ready, target, working }: { ready: number; target: number; working: boolean }) {
  return (
    <div className="grid grid-cols-3 gap-2.5 place-items-center">
      {Array.from({ length: Math.max(target, 1) }).map((_, i) =>
        i < ready ? (
          // Keyed on filled-ness so the pop-in fires exactly when a slot fills.
          <span
            key={`filled-${i}`}
            className="pb-pop-in h-9 w-9 rounded-full bg-emerald-500/25 border border-emerald-400/40 flex items-center justify-center"
          >
            <Users className="h-4 w-4 text-emerald-300" />
          </span>
        ) : (
          <span
            key={`empty-${i}`}
            className={`h-9 w-9 rounded-full border ${
              working && i === ready
                ? "border-white/40 bg-white/10 animate-pulse"
                : "border-dashed border-white/15"
            }`}
          />
        ),
      )}
    </div>
  );
}

/** Magnifying glass sweeping over source lines, with a live topic counter. */
function ResearchStage({ done, total }: { done: number; total: number }) {
  return (
    <div className="relative h-[112px] w-[112px]">
      <div className="absolute inset-x-2 top-3 flex flex-col gap-2.5">
        {[100, 78, 90, 68, 84].map((w, i) => (
          <span key={i} className="h-1.5 rounded-full bg-white/10" style={{ width: `${w}%` }} />
        ))}
      </div>
      <Search className="pb-scan absolute left-3 top-4 h-7 w-7 text-white/45" />
      <p className="absolute -bottom-1 inset-x-0 text-center text-[14px] font-semibold text-white/70 tabular-nums">
        {total > 0 ? `${done} / ${total}` : "…"}
      </p>
    </div>
  );
}

function FinishingStage() {
  return (
    <span className="pb-breathe h-14 w-14 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
      <Sparkles className="h-6 w-6 text-amber-200/80" />
    </span>
  );
}

// ── Step timeline row ────────────────────────────────────────────────────────

function BuildStepRow({ step, bp, active, personaId }: {
  step: (typeof STEPS)[number];
  bp: BuildProgress;
  active: boolean;
  personaId: string;
}) {
  const state = displayState(step.key, bp);
  if (state === "none") return null;
  const Icon = step.icon;
  const detail =
    step.key === "characters"
      ? `${readyAvatarCount(bp)}/${bp.characters.target}`
      : step.key === "research" && bp.research.total > 0
        ? `${bp.research.done}/${bp.research.total}`
        : null;
  return (
    <div
      className="flex items-center gap-2 text-[11.5px] leading-[18px]"
      data-testid={`build-step-${step.key}-${personaId}`}
    >
      <span className="h-4 w-4 flex items-center justify-center flex-shrink-0">
        {state === "complete" ? (
          // Re-keyed on state so the check bounces in the moment the step lands.
          <span key="done" className="pb-check-in flex items-center justify-center h-4 w-4 rounded-full bg-emerald-500/15">
            <Check className="h-2.5 w-2.5 text-emerald-500" strokeWidth={3} />
          </span>
        ) : state === "failed" ? (
          <AlertCircle className="h-3.5 w-3.5 text-destructive" />
        ) : state === "working" ? (
          <span className="relative flex items-center justify-center h-4 w-4">
            <span className="absolute h-3.5 w-3.5 rounded-full bg-primary/20 animate-ping" />
            <Icon className="relative h-3 w-3 text-primary" />
          </span>
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />
        )}
      </span>
      <span
        className={`flex-1 truncate ${
          state === "failed"
            ? "text-destructive/80"
            : active
              ? "text-foreground font-medium"
              : state === "complete"
                ? "text-muted-foreground"
                : "text-muted-foreground/60"
        }`}
      >
        {step.label}
        {state === "failed" ? " — failed" : ""}
      </span>
      {detail && (
        <span className={`tabular-nums text-[11px] ${active ? "text-foreground/80" : "text-muted-foreground/60"}`}>
          {detail}
        </span>
      )}
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────

/**
 * Dedicated build-in-progress persona card: narrates the live pipeline step
 * with step-specific animations and only ever shows build scaffolding — no
 * portrait, summary, chips, or quote leak through before the build resolves.
 */
export type StarterWorkState = "queued" | "running" | "interrupted" | "failed";

export function PersonaBuildCard({
  persona,
  index,
  progress,
  clickable = false,
  starterState,
  starterError,
  onRetry,
  onDiscard,
  recoveryPending = false,
}: {
  persona: { id: string; name: string };
  index: number;
  progress: BuildProgress;
  /** Once the profile has landed the detail page is useful — the card links there. */
  clickable?: boolean;
  starterState?: StarterWorkState;
  starterError?: string | null;
  onRetry?: () => void;
  onDiscard?: () => void;
  recoveryPending?: boolean;
}) {
  const bp = progress;
  const paused = starterState === "interrupted" || starterState === "failed";
  const isWorking = !paused && starterState !== "queued" && !bp.allTerminal;
  const active = paused ? null : activeStep(bp);
  const narration = starterState === "queued"
    ? "Queued — your place is saved and will survive a restart."
    : starterState === "running"
      ? narrationFor(active, bp)
      : starterState === "interrupted"
        ? interruptedNarration(bp, starterError)
        : starterState === "failed"
          ? (starterError || "This starter persona could not be generated.")
          : narrationFor(active, bp);
  const pct = progressPercent(bp);
  const label = starterState === "queued"
    ? "Queued"
    : starterState === "interrupted"
      ? "Interrupted"
      : starterState === "failed"
        ? "Failed"
        : "Building";

  return (
    <Card
      className={`persona-card relative overflow-hidden rounded-2xl h-full ${CARD_MIN_H} border-border/40 ${clickable ? "cursor-pointer hover:border-border/80 transition-all duration-200" : "cursor-default"}`}
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`card-persona-generating-${persona.id}`}
    >
      <CardContent className="p-0 flex flex-col h-full">
        {/* ── Dark band: name + live narration + step stage ─────────── */}
        <div
          className="relative overflow-hidden h-[200px] md:h-[210px] flex-shrink-0 flex flex-col justify-center px-5 py-5"
          style={{ backgroundColor: BAND }}
        >
          <div className="absolute inset-y-0 right-0 w-[45%] bg-gradient-to-l from-white/[0.05] to-transparent flex items-center justify-center pointer-events-none">
            {/* One stage at a time, keyed so it cross-fades when the step advances. */}
            <div key={active ?? "finishing"} className="pb-fade-slide">
              {paused ? (
                <AlertCircle className="h-12 w-12 text-amber-300/70" />
              ) : active === "profile" ? (
                <ProfileStage />
              ) : active === "image" ? (
                <PortraitStage />
              ) : active === "enrichment" ? (
                <TraitsStage />
              ) : active === "characters" ? (
                <CharactersStage
                  ready={readyAvatarCount(bp)}
                  target={bp.characters.target}
                  working
                />
              ) : active === "research" ? (
                <ResearchStage done={bp.research.done} total={bp.research.total} />
              ) : (
                <FinishingStage />
              )}
            </div>
          </div>
          <div className="relative z-10 max-w-[58%] flex flex-col gap-2.5 overflow-hidden">
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-[0.18em] text-white/70">
              <Users className="h-3 w-3" />Persona
            </span>
            {/* Full name always shown — type steps down for long names. */}
            <h3
              className={`${PERSONA_NAME_TIER_COMPACT[personaNameTier(persona.name)]} text-[#f3ede3]`}
              style={SERIF}
              data-testid={`text-name-${persona.id}`}
            >
              {persona.name}
            </h3>
            {/* Fixed-height narration slot: copy swaps without layout shift. */}
            <div className="h-[36px]">
              <p
                key={active ?? "finishing"}
                className="pb-fade-slide text-[12px] leading-[18px] text-white/65 line-clamp-2"
                data-testid={`build-narration-${persona.id}`}
              >
                {active === "profile" ? <Typewriter text={narration} /> : narration}
              </p>
            </div>
          </div>
        </div>

        {/* ── Overall progress (real step completion, never a timer) ── */}
        <div className="px-5 pt-4" data-testid={`build-progress-bar-${persona.id}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="inline-flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {isWorking && (
                <Loader2
                  className="h-3 w-3 text-primary motion-safe:animate-spin"
                  aria-hidden="true"
                  data-testid={`build-working-indicator-${persona.id}`}
                />
              )}
              {isWorking ? "Building persona" : label}
            </span>
            <span className="text-[10.5px] font-semibold tabular-nums text-muted-foreground">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-700 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* ── Step timeline ─────────────────────────────────────────── */}
        <div className="px-5 pt-3.5 space-y-2" data-testid={`build-progress-${persona.id}`}>
          {STEPS.map((s) => (
            <BuildStepRow key={s.key} step={s} bp={bp} active={active === s.key} personaId={persona.id} />
          ))}
        </div>

        {/* ── Footer ────────────────────────────────────────────────── */}
        <div className="mt-auto flex items-center justify-between gap-2 px-4 py-3">
          {paused ? (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="h-7 px-3 text-[11px]"
                disabled={recoveryPending}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onRetry?.();
                }}
                data-testid={`button-retry-starter-${persona.id}`}
              >
                Retry
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2.5 text-[11px] text-muted-foreground"
                disabled={recoveryPending}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onDiscard?.();
                }}
                data-testid={`button-discard-starter-${persona.id}`}
              >
                Discard
              </Button>
            </div>
          ) : <span />}
          <Badge className={`text-[9px] font-semibold uppercase tracking-widest rounded-full px-2.5 h-5 border ${
            paused
              ? "bg-amber-500/10 text-amber-700 border-amber-500/20"
              : "bg-primary/10 text-primary border-primary/20"
          }`}>
            {label}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
